/**
 * Login — editorial split-screen sign-in.
 *
 *   Left  — deep-burgundy brand panel. A statement of purpose set over an
 *           engineering grid + paper grain, with capability chips and an
 *           editorial pull-quote. Mirrors the procurement app's login, but
 *           in burgundy so the brand color reads as dominant.
 *   Right — sharp paper form. Eyebrow labels, a display welcome headline,
 *           and the burgundy submit button as the only filled element.
 *           A quiet trust footer grounds it as a serious enterprise tool.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  ArrowForward,
  AutoAwesome,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import api from '../../services/api';

// Small caps-style category label used across both panels.
const eyebrowSx = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.14em',
  fontFamily: 'var(--font-body)',
  lineHeight: 1,
};

// Faint fractal-noise paper grain, matching the procurement canvas.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.05 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const CAPABILITIES = [
  'Demand forecasting',
  'Replenishment',
  'Inventory',
  'AI insights',
  'Sage X3',
  'Multi-store',
];

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 'var(--radius-md)',
      fontFamily: 'var(--font-body)',
      fontSize: '14px',
      bgcolor: 'var(--color-surface)',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--color-accent)',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--color-accent)',
        borderWidth: '1.5px',
      },
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', fontFamily: 'var(--font-body)' }}>
      {/* ─── Left — deep-burgundy brand panel ─── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: { md: '50%', lg: '55%' },
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          background: 'linear-gradient(157deg, #4E1421 0%, #380E18 100%)',
        }}
      >
        {/* Engineering grid, masked to a soft ellipse */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.16,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            maskImage: 'radial-gradient(ellipse at 28% 18%, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 28% 18%, black 30%, transparent 75%)',
          }}
        />
        {/* Radial glows — rose highlight + burgundy depth */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(55% 45% at 18% 12%, rgba(240,184,192,0.20), transparent 62%), radial-gradient(50% 50% at 92% 88%, rgba(139,36,56,0.40), transparent 60%)',
          }}
        />
        {/* Paper grain */}
        <Box
          aria-hidden
          sx={{ position: 'absolute', inset: 0, opacity: 0.5, backgroundImage: GRAIN }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            px: { md: 6, lg: 8 },
            py: 8,
          }}
        >
          {/* Brand mark */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                height: 44,
                width: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.02))',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)',
              }}
            >
              <img src="/IRIS-LOGO.png" alt="IRIS" style={{ width: 28, height: 'auto' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
              <Typography
                sx={{ fontSize: '18px', fontWeight: 600, fontFamily: 'var(--font-heading)', letterSpacing: '0.04em' }}
              >
                IRIS
              </Typography>
              <Typography sx={{ ...eyebrowSx, color: 'rgba(255,255,255,0.55)' }}>
                Retail Intelligence
              </Typography>
            </Box>
          </Box>

          {/* Statement */}
          <Box sx={{ maxWidth: 520 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(255,255,255,0.15)',
                bgcolor: 'rgba(255,255,255,0.05)',
                px: 1.5,
                py: 0.75,
                mb: 4,
              }}
            >
              <AutoAwesome sx={{ fontSize: 13, color: '#F0B8C0' }} />
              <Typography sx={{ ...eyebrowSx, color: 'rgba(255,255,255,0.72)' }}>
                Built for luxury retail distribution
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: 'var(--font-heading)',
                fontSize: { md: '2.75rem', lg: '3.25rem' },
                fontWeight: 600,
                lineHeight: 1.03,
                letterSpacing: '-0.02em',
                mb: 3,
              }}
            >
              The right stock,
              <br />
              <Box component="span" sx={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.75)' }}>
                right on time.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: '16px',
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.66)',
                maxWidth: 440,
                fontFamily: 'var(--font-body)',
              }}
            >
              Real-time demand forecasting, replenishment, and inventory
              intelligence — one console for the people who actually run
              retail distribution.
            </Typography>

            {/* Capability chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 3 }}>
              {CAPABILITIES.map((chip) => (
                <Box
                  key={chip}
                  sx={{
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    px: 1.25,
                    py: 0.6,
                  }}
                >
                  <Typography sx={{ ...eyebrowSx, color: 'rgba(255,255,255,0.66)' }}>{chip}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Editorial pull-quote */}
          <Box
            component="figure"
            sx={{ m: 0, maxWidth: 440, borderInlineStart: '2px solid rgba(255,255,255,0.15)', pl: 2.5 }}
          >
            <Typography
              component="blockquote"
              sx={{
                m: 0,
                fontFamily: 'var(--font-heading)',
                fontSize: '18px',
                fontStyle: 'italic',
                lineHeight: 1.4,
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              “IRIS replaced a dozen spreadsheets. We see stockouts coming
              days before they happen.”
            </Typography>
            <Typography component="figcaption" sx={{ ...eyebrowSx, color: 'rgba(255,255,255,0.45)', mt: 1.5 }}>
              Head of Supply · Pilot deployment
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ─── Right — paper form ─── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'var(--color-bg-primary)',
          px: { xs: 3, lg: 6 },
          py: 6,
        }}
      >
        {/* Eyebrow header */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'var(--color-text-muted)',
          }}
        >
          <Typography sx={eyebrowSx}>Sign in</Typography>
          <Typography sx={{ ...eyebrowSx, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>
            Inventory OS
          </Typography>
        </Box>

        {/* Form block */}
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
            <img src="/IRIS-LOGO.png" alt="IRIS Logo" style={{ width: 96, height: 'auto' }} />
          </Box>

          <Typography
            sx={{
              fontFamily: 'var(--font-heading)',
              fontSize: '2rem',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              color: 'var(--color-text-primary)',
              mb: 1,
            }}
          >
            Welcome back to{' '}
            <Box component="span" sx={{ color: 'var(--color-accent)' }}>
              IRIS
            </Box>
            .
          </Typography>
          <Typography
            sx={{ fontSize: '14px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)', mb: 4 }}
          >
            Sign in with your credentials to access your inventory console.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 'var(--radius-md)', fontSize: '13px' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography sx={{ ...eyebrowSx, color: 'var(--color-text-secondary)', mb: 0.75 }}>Email</Typography>
            <TextField
              size="small"
              required
              fullWidth
              id="email"
              placeholder="name@company.com"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputProps={{ 'aria-label': 'Email address' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.5, ...fieldSx }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography sx={{ ...eyebrowSx, color: 'var(--color-text-secondary)' }}>Password</Typography>
            </Box>
            <TextField
              size="small"
              required
              fullWidth
              name="password"
              placeholder="Enter your password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputProps={{ 'aria-label': 'Password' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ fontSize: 18, color: 'var(--color-text-muted)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: 'var(--color-text-muted)' }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3.5, ...fieldSx }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              disableElevation
              endIcon={!loading ? <ArrowForward sx={{ fontSize: 18 }} /> : undefined}
              sx={{
                height: 46,
                justifyContent: 'space-between',
                px: 2,
                bgcolor: 'var(--color-accent)',
                color: '#FFFFFF',
                fontFamily: 'var(--font-body)',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                borderRadius: 'var(--radius-md)',
                textTransform: 'none',
                transition: 'all 200ms ease-out',
                '&:hover': { bgcolor: 'var(--color-accent-hover)' },
                '&.Mui-disabled': { bgcolor: 'var(--color-accent)', color: 'rgba(255,255,255,0.6)' },
              }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'Sign in'}
            </Button>
          </Box>
        </Box>

        {/* Trust footer */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            color: 'var(--color-text-disabled)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VerifiedUserOutlined sx={{ fontSize: 13 }} />
            <Typography sx={eyebrowSx}>Encrypted sign-in</Typography>
          </Box>
          <Typography sx={{ ...eyebrowSx, fontFamily: 'var(--font-mono)', textTransform: 'none', letterSpacing: '0.04em' }}>
            IRIS · Retail Inventory System
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
